import { useState, useEffect, useRef } from "react";
import {
  X,
  Lock,
  CreditCard,
  Loader,
  CheckCircle,
  XCircle,
} from "lucide-react";
import BaseApi from "../services/base-api";
import { useTranslation } from "react-i18next";
import Price from "../components/Currency/Price";
import { useCurrency } from "../components/Currency/CurrencyContext";
import { success } from "../utils/toastHelper";

interface MyFatoorahPaymentProps {
  orderId: string;
  amount: number;
  onSuccess: () => void;
  onCancel: () => void;
  onError: (error: string) => void;
  paymentMethod?: string;
  paymentProvider?: string;
}

interface PaymentSession {
  sessionId: string;
  paymentUrl: string;
}

interface PaymentMethod {
  paymentMethodId: string;
  paymentMethodName: string;
  paymentMethodCode: string;
  isDirectPayment: boolean;
  serviceCharge: number;
  totalAmount: number;
  currencyIso: string;
  imageUrl: string;
}

const normalizePaymentProvider = (value?: string) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const getKnownPaymentProvider = (provider?: string, method?: string) => {
  const normalizedProvider = normalizePaymentProvider(provider);
  if (["myfatoorah", "skipcash"].includes(normalizedProvider)) {
    return provider;
  }

  const normalizedMethod = normalizePaymentProvider(method);
  if (["myfatoorah", "skipcash"].includes(normalizedMethod)) {
    return method;
  }

  return undefined;
};

const MyFatoorahPayment = ({
  orderId,
  amount,
  paymentMethod,
  paymentProvider,
  onSuccess,
  onCancel,
  onError,
}: MyFatoorahPaymentProps) => {
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const [isLoading, setIsLoading] = useState(false);
  const [paymentSession, setPaymentSession] = useState<PaymentSession | null>(
    null,
  );
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "processing" | "verifying" | "success" | "failed"
  >("idle");
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | undefined
  >(paymentMethod);
  const [activePaymentProvider, setActivePaymentProvider] = useState<
    string | undefined
  >(getKnownPaymentProvider(paymentProvider, paymentMethod));
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("userData");
    if (storedUser) return JSON.parse(storedUser);
    return {
      firstName: "",
      lastName: "",
      email: "",
      phoneCode: "",
      phone: "",
      preferences: {
        language: "en",
        notifications: {
          email: true,
          whatsapp: true,
        },
      },
    };
  });

  // Refs for polling and window management
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const paymentWindowRef = useRef<Window | null>(null);
  const pollCountRef = useRef(0);
  const maxPollAttempts = 180; // 180 attempts * 2 seconds = 6 minutes
  const lastStatusRef = useRef<string>("");

  // Fetch available payment methods on mount
  useEffect(() => {
    fetchPaymentMethods(amount, currency.symbol);
  }, []);

  useEffect(() => {
    setActivePaymentProvider(
      getKnownPaymentProvider(paymentProvider, paymentMethod),
    );
  }, [paymentProvider, paymentMethod]);

  useEffect(() => {
    if (paymentProvider) return;

    const fetchActivePaymentProvider = async () => {
      try {
        const response = await BaseApi.get("/settings/public");
        const provider =
          response?.data?.payment_provider || response?.data?.paymentProvider;

        if (provider) {
          setActivePaymentProvider(provider);
        }
      } catch (error) {
        console.error("Error fetching payment provider:", error);
      }
    };

    fetchActivePaymentProvider();
  }, [paymentProvider]);

  // Listen for messages from payment callback page
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin matches your domain
      if (!event.origin.includes(window.location.hostname)) {
        return;
      }

      const { type, orderId: messageOrderId, status } = event.data;

      if (type === "PAYMENT_CALLBACK" && messageOrderId === orderId) {
        // console.log("Received payment callback:", status);

        if (status === "success" || status === "paid") {
          handlePaymentSuccess();
        } else if (status === "failed" || status === "error") {
          handlePaymentFailure();
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      stopPolling();
      closePaymentWindow();
    };
  }, [orderId]);

  // Fetch available payment methods from API
  const fetchPaymentMethods = async (amount: number, currency: string) => {
    try {
      const response = await BaseApi.get("/payments/methods", {
        amount,
        currency,
      });

      if (response?.data?.paymentMethods) {
        setPaymentMethods(response.data.paymentMethods);
      }
    } catch (error: any) {
      console.error("Error fetching payment methods:", error);
    }
  };

  // Close payment window
  const closePaymentWindow = () => {
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      paymentWindowRef.current.close();
    }
    paymentWindowRef.current = null;
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    pollCountRef.current = 0;
  };

  // Handle payment success
  const handlePaymentSuccess = () => {
    stopPolling();
    closePaymentWindow();
    setPaymentStatus("success");
    success(t("Payment completed successfully!"));

    setTimeout(() => {
      onSuccess();
    }, 2000);
  };

  // Handle payment failure
  const handlePaymentFailure = () => {
    stopPolling();
    closePaymentWindow();
    setPaymentStatus("failed");
    onError(t("Payment failed or was cancelled"));
  };

  // Start polling for payment status
  const startPolling = () => {
    stopPolling(); // Clear any existing polling
    pollCountRef.current = 0;
    lastStatusRef.current = "";

    pollingIntervalRef.current = setInterval(async () => {
      pollCountRef.current++;

      // Check if payment window is closed
      if (paymentWindowRef.current && paymentWindowRef.current.closed) {
        // console.log("Payment window was closed by user");
        stopPolling();
        await checkPaymentStatus(true);
        return;
      }

      // Check payment status
      await checkPaymentStatus(false);

      // Stop polling after max attempts
      if (pollCountRef.current >= maxPollAttempts) {
        stopPolling();
        closePaymentWindow();
        setPaymentStatus("failed");
        onError(
          t("Payment verification timeout. Please check your order status."),
        );
      }
    }, 2000); // Poll every 2 seconds
  };

  // Check payment status
  const checkPaymentStatus = async (isFinalCheck: boolean = false) => {
    try {
      const response = await BaseApi.get(`/payments/status/${orderId}`);
      const paymentStatus = response?.data?.paymentStatus;
      // const orderStatus = response?.data?.orderStatus;
      // const invoice = response?.data?.invoice;

      // Only process if status changed
      if (paymentStatus === lastStatusRef.current && !isFinalCheck) {
        return;
      }

      lastStatusRef.current = paymentStatus;

      if (response?.success && paymentStatus === "paid") {
        setPaymentStatus(paymentStatus);
        handlePaymentSuccess();
      } else if (paymentStatus === "failed" || paymentStatus === "cancelled") {
        setPaymentStatus(paymentStatus);
        handlePaymentFailure();
      } else if (paymentStatus === "pending" && isFinalCheck) {
        setPaymentStatus(paymentStatus);
        onError(t("Payment is still pending. Please check your order status."));
      }
    } catch (error: any) {
      console.error("Error checking payment status:", error);

      if (isFinalCheck) {
        setPaymentStatus("failed");
        onError(t("Failed to verify payment status"));
      }
    }
  };

  // Initialize payment session
  const initiatePayment = async () => {
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      onError(t("Please select a payment method"));
      return;
    }

    setIsLoading(true);
    setPaymentStatus("processing");

    try {
      const response = await BaseApi.post("/payments/initiate", {
        orderId,
      });

      if (
        response.success &&
        response?.data?.transactionId &&
        response?.data?.paymentURL
      ) {
        setPaymentSession({
          sessionId: response.data.transactionId,
          paymentUrl: response.data.paymentURL,
        });

        // Open in same tab/page instead of popup window
        window.location.assign(response.data.paymentURL);
        return;
      } else {
        setPaymentStatus("failed");
        onError(
          response?.message ||
            t("Failed to initialize payment. Please try again."),
        );
      }
    } catch (error: any) {
      console.error("Payment initiation error:", error);
      setPaymentStatus("failed");
      onError(
        error?.response?.data?.message ||
          error?.message ||
          t("Failed to initialize payment. Please try again."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manual status check
  const handleManualCheck = async () => {
    setPaymentStatus("verifying");
    await checkPaymentStatus(true);
  };

  // Reset payment state
  const resetPayment = () => {
    stopPolling();
    closePaymentWindow();
    setPaymentStatus("idle");
    setPaymentSession(null);
    setSelectedPaymentMethod(paymentMethod);
  };

  // Handle cancel
  const handleCancel = () => {
    stopPolling();
    closePaymentWindow();
    onCancel();
  };

  const normalizedPaymentProvider =
    normalizePaymentProvider(activePaymentProvider);
  const showMyFatoorahSecurityLabel =
    normalizedPaymentProvider === "myfatoorah";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-black via-gray-700 to-black px-6 py-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Lock className="w-6 h-6" />
            {t("Secure payment")}
          </h3>
          <button
            onClick={handleCancel}
            className="text-white hover:text-gray-300 transition-colors p-1"
            disabled={
              paymentStatus === "verifying" || paymentStatus === "success"
            }
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Initial State - Before Payment */}
          {paymentStatus === "idle" && (
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-[#0d223f] rounded-full mx-auto mb-6 flex items-center justify-center">
                  <CreditCard size={40} className="text-blue-600" />
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t("Complete Your Payment")}
                </h2>

                <div className="bg-gray-50 rounded-xl p-6 mb-6 max-w-md mx-auto">
                  <div className="flex flex-col justify-between items-center mb-4 pb-4 border-b border-gray-200">
                    <span className="text-gray-600">{t("Order ID")}</span>
                    <span className="font-bold text-gray-900">{orderId}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t("Amount to Pay")}</span>
                    <span className="text-2xl font-bold text-blue-600">
                      <Price amount={Number(amount)} />
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Methods Selection */}
              {paymentMethods.length > 0 && !selectedPaymentMethod && (
                <div className="max-w-2xl mx-auto mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t("Select Payment Method")}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {paymentMethods.map((method) => (
                      <button
                        key={method.paymentMethodId}
                        onClick={() =>
                          setSelectedPaymentMethod(method.paymentMethodId)
                        }
                        className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-[#0d223f] hover:bg-[#0d223f] transition-all"
                      >
                        {method.imageUrl && (
                          <img
                            src={method.imageUrl}
                            alt={method.paymentMethodName}
                            className="w-12 h-12 object-contain"
                          />
                        )}
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-gray-900">
                            {method.paymentMethodName}
                          </div>
                          {method.serviceCharge > 0 && (
                            <div className="text-sm text-gray-600">
                              {t("Fee")}:{" "}
                              <Price amount={method.serviceCharge} />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Proceed Button */}
              <div className="text-center">
                <button
                  onClick={initiatePayment}
                  disabled={
                    isLoading ||
                    (!selectedPaymentMethod && paymentMethods.length > 0)
                  }
                  className="bg-black hover:bg-gray-800 text-white font-bold py-4 px-8 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>{t("Initializing...")}</span>
                    </>
                  ) : (
                    <>
                      <Lock size={20} />
                      <span>{t("Proceed to Pay")}</span>
                    </>
                  )}
                </button>

                {showMyFatoorahSecurityLabel && (
                  <p className="text-sm text-gray-600 mt-6 flex items-center justify-center gap-2">
                    <Lock size={16} />
                    <span>
                      {t("Secured by MyFatoorah - Your payment is encrypted")}
                    </span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Payment Processing - Waiting for completion */}
          {paymentStatus === "processing" && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-[#0d223f] rounded-full mx-auto mb-6 flex items-center justify-center">
                <Loader size={40} className="text-blue-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t("Redirecting to payment")}
              </h2>
              <p className="text-gray-600 mb-6">
                {t("Please wait while we open the secure payment page")}
              </p>
            </div>
          )}

          {/* Verifying Payment */}
          {paymentStatus === "verifying" && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-[#0d223f] rounded-full mx-auto mb-6 flex items-center justify-center">
                <Loader size={40} className="text-blue-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t("Verifying Payment")}
              </h2>
              <p className="text-gray-600">
                {t("Please wait while we confirm your payment...")}
              </p>
            </div>
          )}

          {/* Payment Success */}
          {paymentStatus === "success" && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center animate-bounce-in">
                <CheckCircle size={40} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t("Payment Successful!")}
              </h2>
              <p className="text-gray-600 mb-4">
                {t("Your payment has been processed successfully")}
              </p>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 max-w-md mx-auto mb-4">
                <p className="text-sm text-green-800">
                  {t("Order ID")}: <span className="font-bold">{orderId}</span>
                </p>
              </div>
              <p className="text-sm text-gray-600">{t("Redirecting...")}</p>
            </div>
          )}

          {/* Payment Failed */}
          {paymentStatus === "failed" && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                <XCircle size={40} className="text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t("Payment Failed")}
              </h2>
              <p className="text-gray-600 mb-6">
                {t("There was an issue processing your payment")}
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleCancel}
                  className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-all font-medium"
                >
                  {t("Close")}
                </button>
                <button
                  onClick={resetPayment}
                  className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-all font-medium"
                >
                  {t("Try Again")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes bounce-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .animate-bounce-in {
          animation: bounce-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MyFatoorahPayment;
